import "reflect-metadata";
import { ReactNode, useEffect } from "react";
import { Toaster } from "react-hot-toast";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
} from "react-router";
import { ThemeProvider } from "flowbite-react";
import { ErrorBoundaryReactiv } from "./components/ErrorBoundaryReactiv";
import { getAppConfig } from "./utils/app.server";
import { theme } from "./styles/theme";
import "~/styles/main.css";
import i18nServer from "./modules/i18n.server";
import type { LoaderFunctionArgs } from "react-router";
import { useTranslation } from "react-i18next";

/**
 * Simple replacement for remix-i18next's useChangeLanguage
 * that doesn't use useNavigate() (which isn't available in React Router v7 root)
 * This should change when upgrading to remix-i18next v7, which is compatible with React Router v7
 */
function useChangeLanguage(locale: string) {
  const { i18n } = useTranslation();
  useEffect(() => {
    if (i18n.language !== locale) {
      i18n.changeLanguage(locale);
    }
  }, [locale, i18n]);
}

export async function loader({ request }: LoaderFunctionArgs) {
  const { isProd } = getAppConfig();
  const locale = await i18nServer.getLocale(request);
  return {
    SENTRY_DSN: process.env.SENTRY_DSN || "",
    SENTRY_ENVIRONMENT: process.env.SENTRY_ENVIRONMENT || "",
    SENTRY_RELEASE: process.env.SENTRY_RELEASE || "",
    PROD: isProd,
    locale,
  };
}

export const handle = {
  i18n: ["common", "studio"],
};

function ErrorBoundaryDocument(props: { children: ReactNode; title?: string }) {
  // In error boundaries, loader data may not be available, so use a default
  const locale = "en";

  return (
    <html lang={locale}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="!bg-gray-50">
        <div>
          <Toaster />
        </div>
        <ThemeProvider theme={theme}>{props.children}</ThemeProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function App() {
  const { SENTRY_DSN, SENTRY_ENVIRONMENT, SENTRY_RELEASE, PROD, locale } =
    useLoaderData<typeof loader>();
  useChangeLanguage(locale);

  return (
    <html lang={locale}>
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        {import.meta.env.DEV && <script src="http://localhost:8097" />}
        <Meta />
        <Links />
        <script
          dangerouslySetInnerHTML={{
            __html: `
              window.SENTRY_DSN = '${SENTRY_DSN}';
              window.SENTRY_ENVIRONMENT = '${SENTRY_ENVIRONMENT}';
              window.SENTRY_RELEASE = '${SENTRY_RELEASE}';
            `,
          }}
        ></script>
        {PROD && (
          <>
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=G-XK8V6KJF3L`}
            />
            <script
              async
              id="gtag-init"
              dangerouslySetInnerHTML={{
                __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'G-XK8V6KJF3L', {
                  page_path: window.location.pathname,
                  cookie_flags: 'max-age=7200;secure;samesite=none'
                });
              `,
              }}
            />
          </>
        )}
      </head>
      <body className="!bg-gray-50">
        <div>
          <Toaster />
        </div>
        <ThemeProvider theme={theme}>
          <Outlet />
        </ThemeProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export const ErrorBoundary = () => {
  return (
    <ErrorBoundaryDocument>
      <ErrorBoundaryReactiv />
    </ErrorBoundaryDocument>
  );
};

export default App;
